import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { MagnifyingGlass, Funnel, X, ArrowLeft, ArrowRight, GlobeSimple, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FavoriteButton } from "@/components/FavoriteButton";

const STATUS_COLORS = {
  Vigente: "bg-[#2A9D8F] text-white",
  Adjudicado: "bg-[#002FA7] text-white",
  Desierto: "bg-[#E63946] text-white",
  "Evaluación": "bg-amber-500 text-white",
  Prescindido: "bg-orange-600 text-white",
  Suspendido: "bg-gray-500 text-white",
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ tenders: [], total: 0, page: 1, pages: 1 });
  const [categories, setCategories] = useState([]);
  const [entities, setEntities] = useState([]);
  const [filters, setFilters] = useState({
    search: "", category: "", entity: "", status: "",
    date_from: "", date_to: "", amount_min: "", amount_max: "",
  });
  const [nogInput, setNogInput] = useState("");
  const [nogLoading, setNogLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/tenders/categories").then(r => setCategories(r.data.categories)).catch(() => {});
    api.get("/tenders/entities").then(r => setEntities(r.data.entities)).catch(() => {});
  }, []);

  const fetchTenders = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("page", p);
      params.append("limit", 15);
      const res = await api.get(`/tenders?${params.toString()}`);
      setResults(res.data);
      setPage(p);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchTenders(1); }, [fetchTenders]);

  const clearFilters = () => {
    setFilters({ search: "", category: "", entity: "", status: "", date_from: "", date_to: "", amount_min: "", amount_max: "" });
  };

  const handleNogLookup = async () => {
    if (!nogInput.trim()) return;
    setNogLoading(true);
    try {
      const res = await api.post("/scraper/lookup-nog", { nog: nogInput.trim() });
      if (res.data.status === "found") {
        toast.success(`NOG ${nogInput} encontrado y guardado`);
        navigate(`/licitacion/${res.data.tender.id}`);
      } else if (res.data.status === "exists") {
        toast.info(`NOG ${nogInput} ya existe en la base de datos`);
        navigate(`/licitacion/${res.data.tender.id}`);
      } else {
        toast.warning(res.data.message || "No se pudo obtener datos del NOG");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al buscar NOG");
    } finally { setNogLoading(false); }
  };

  return (
    <div data-testid="search-page" className="space-y-6">
      <h1 className="font-heading font-black text-3xl tracking-tight">Buscador de Licitaciones</h1>

      {/* NOG Direct lookup */}
      <Card className="rounded-none shadow-none border-2 border-[#002FA7]/20 bg-[#002FA7]/[0.02]" data-testid="nog-lookup-panel">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <GlobeSimple size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
            <div className="flex-shrink-0">
              <p className="text-sm font-medium">Búsqueda directa en GuateCompras</p>
              <p className="text-xs text-muted-foreground">Ingresa un NOG para buscar directamente en el portal</p>
            </div>
            <div className="flex gap-2 flex-1 min-w-[280px]">
              <Input
                data-testid="nog-input"
                type="text"
                placeholder="Ej: 25318276"
                className="rounded-none h-10 bg-card font-mono flex-1"
                value={nogInput}
                onChange={(e) => setNogInput(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleNogLookup()}
              />
              <Button
                data-testid="nog-lookup-button"
                className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white px-6"
                onClick={handleNogLookup}
                disabled={nogLoading || !nogInput.trim()}
              >
                {nogLoading ? <Spinner size={16} className="animate-spin" /> : "Buscar NOG"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filters sidebar */}
        <Card className="rounded-none shadow-none border border-border/40 lg:w-72 flex-shrink-0" data-testid="filters-panel">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-sm flex items-center gap-2">
              <Funnel size={16} weight="duotone" className="text-[#002FA7]" /> Filtros
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Búsqueda</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
                <Input
                  data-testid="search-input"
                  placeholder="NOG, título, entidad..."
                  className="rounded-none pl-9 h-10 bg-card"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Categoría</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="category-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Estado</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="status-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Vigente">Vigente</SelectItem>
                  <SelectItem value="En evaluación">En evaluación</SelectItem>
                  <SelectItem value="Adjudicado">Adjudicado</SelectItem>
                  <SelectItem value="Desierto">Desierto</SelectItem>
                  <SelectItem value="Prescindido">Prescindido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Entidad</Label>
              <Select value={filters.entity} onValueChange={(v) => setFilters({ ...filters, entity: v === "all" ? "" : v })}>
                <SelectTrigger className="rounded-none h-10 bg-card" data-testid="entity-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todas</SelectItem>
                  {entities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Monto mín.</Label>
                <Input data-testid="amount-min" type="number" placeholder="0" className="rounded-none h-9 text-sm bg-card" value={filters.amount_min} onChange={(e) => setFilters({ ...filters, amount_min: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Monto máx.</Label>
                <Input data-testid="amount-max" type="number" placeholder="999M" className="rounded-none h-9 text-sm bg-card" value={filters.amount_max} onChange={(e) => setFilters({ ...filters, amount_max: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Desde</Label>
                <Input data-testid="date-from" type="date" className="rounded-none h-9 text-sm bg-card" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Hasta</Label>
                <Input data-testid="date-to" type="date" className="rounded-none h-9 text-sm bg-card" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="apply-filters" className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white text-xs" onClick={() => fetchTenders(1)}>
                Buscar
              </Button>
              <Button data-testid="clear-filters" variant="outline" className="rounded-none text-xs" onClick={clearFilters}>
                <X size={14} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground font-mono" data-testid="results-count">
              {results.total} resultados encontrados
            </p>
          </div>
          <Card className="rounded-none shadow-none border border-border/40" data-testid="results-table">
            {loading ? (
              <CardContent className="p-6 space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 rounded-none" />)}
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider">NOG</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider">Título</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider">Entidad</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider">Categoría</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Monto</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.tenders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                          No se encontraron licitaciones
                        </TableCell>
                      </TableRow>
                    ) : (
                      results.tenders.map((t, i) => (
                        <TableRow
                          key={t.id}
                          className="cursor-pointer hover:bg-secondary/50 transition-colors duration-150"
                          onClick={() => navigate(`/licitacion/${t.id}`)}
                          data-testid={`search-result-${i}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <FavoriteButton tenderId={t.id} size={16} />
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{t.nog}</TableCell>
                          <TableCell className="text-sm font-medium max-w-xs truncate">{t.title}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{t.entity_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{t.category}</TableCell>
                          <TableCell className="font-mono text-sm text-right">Q{(t.amount || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-none text-[10px] font-mono ${STATUS_COLORS[t.status] || "bg-secondary text-foreground"}`}>
                              {t.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
          {/* Pagination */}
          {results.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs font-mono text-muted-foreground">
                Página {results.page} de {results.pages}
              </p>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  disabled={page <= 1}
                  onClick={() => fetchTenders(page - 1)}
                  data-testid="prev-page"
                >
                  <ArrowLeft size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  disabled={page >= results.pages}
                  onClick={() => fetchTenders(page + 1)}
                  data-testid="next-page"
                >
                  <ArrowRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
